import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  TrendingUp, Users, Calendar, Clock, DollarSign, AlertTriangle,
  ArrowLeft, BarChart3, CheckCircle2, XCircle, Download, Activity, UserCheck, UserX
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AnalyticsDashboard({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => { fetchAnalytics(); }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/analytics/dashboard?period=${period}`, { withCredentials: true });
      setData(d);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-6 text-muted-foreground">Loading analytics...</div>;
  if (!data) return <div className="p-6 text-red-400">Failed to load analytics</div>;

  const s = data.shifts;
  const w = data.workforce;
  const r = data.revenue;
  const weekly = data.weekly_breakdown || [];
  const util = data.utilisation || {};
  const costs = data.shift_costs || {};
  const maxWeeklyTotal = Math.max(...weekly.map(wk => wk.total), 1);

  return (
    <div className="p-6 space-y-6" data-testid="analytics-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack} className="text-muted-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">Analytics & Reports</h2>
            <p className="text-sm text-muted-foreground">Period: {period}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="border-border text-muted-foreground hover:bg-muted/50"
            onClick={() => window.open(`${API_URL}/api/export/analytics/pdf?period=${period}`, '_blank')}
            data-testid="export-analytics-pdf">
            <Download className="w-4 h-4 mr-1.5" />PDF
          </Button>
          <Button variant="outline" size="sm" className="border-border text-muted-foreground hover:bg-muted/50"
            onClick={() => window.open(`${API_URL}/api/export/analytics/csv?period=${period}`, '_blank')}
            data-testid="export-analytics-csv">
            <Download className="w-4 h-4 mr-1.5" />CSV
          </Button>
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
            className="bg-muted border-border text-foreground w-40"
            data-testid="analytics-period" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Completion Rate</span>
              <TrendingUp className="w-4 h-4 text-[#A8E6CF]" />
            </div>
            <p className="text-2xl font-bold text-[#A8E6CF]">{s.completion_rate}%</p>
            <p className="text-xs text-muted-foreground">{s.completed}/{s.total} shifts</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Total Payroll</span>
              <DollarSign className="w-4 h-4 text-[#FF6F61]" />
            </div>
            <p className="text-2xl font-bold text-[#FF6F61]">${r.total_payroll.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">{r.payslips_issued} payslips</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Workforce</span>
              <Users className="w-4 h-4 text-[#00A6A0]" />
            </div>
            <p className="text-2xl font-bold text-foreground">{w.total_caregivers}</p>
            <p className="text-xs text-muted-foreground">{w.total_clients} clients</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">No-Shows</span>
              <AlertTriangle className="w-4 h-4 text-red-400" />
            </div>
            <p className="text-2xl font-bold text-red-400">{data.alerts.no_shows}</p>
            <p className="text-xs text-muted-foreground">This period</p>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Shift Completion Chart */}
      <Card className="glass-card border-border" data-testid="weekly-chart-card">
        <CardHeader>
          <CardTitle className="text-base text-foreground flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#00A6A0]" />Weekly Shift Completion
          </CardTitle>
        </CardHeader>
        <CardContent>
          {weekly.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No weekly data for this period</p>
          ) : (
            <div className="space-y-4">
              {/* Bar chart */}
              <div className="flex items-end gap-2 h-40" data-testid="weekly-bars">
                {weekly.map(wk => {
                  const h = Math.max((wk.total / maxWeeklyTotal) * 100, 4);
                  const compH = wk.total > 0 ? (wk.completed / wk.total) * h : 0;
                  const cancelH = wk.total > 0 ? (wk.cancelled / wk.total) * h : 0;
                  const noShowH = wk.total > 0 ? (wk.no_show / wk.total) * h : 0;
                  return (
                    <div key={wk.week} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] text-muted-foreground">{wk.completion_rate}%</span>
                      <div className="w-full rounded-t-md overflow-hidden flex flex-col-reverse" style={{ height: `${h}%` }}>
                        <div className="bg-[#A8E6CF]" style={{ height: `${compH}%` }} title={`${wk.completed} completed`} />
                        <div className="bg-amber-500" style={{ height: `${cancelH}%` }} title={`${wk.cancelled} cancelled`} />
                        <div className="bg-red-500" style={{ height: `${noShowH}%` }} title={`${wk.no_show} no-show`} />
                        <div className="bg-blue-500/30 flex-1" />
                      </div>
                      <span className="text-[10px] text-muted-foreground font-medium">{wk.week}</span>
                      <span className="text-[9px] text-muted-foreground">{wk.total} total</span>
                    </div>
                  );
                })}
              </div>
              {/* Legend */}
              <div className="flex items-center gap-4 justify-center text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#A8E6CF]" />Completed</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500" />Cancelled</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500" />No-Show</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500/30" />Open</span>
              </div>
              {/* Weekly table */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="weekly-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-muted-foreground font-medium">Week</th>
                      <th className="text-center py-2 text-muted-foreground font-medium">Total</th>
                      <th className="text-center py-2 text-muted-foreground font-medium">Done</th>
                      <th className="text-center py-2 text-muted-foreground font-medium">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekly.map(wk => (
                      <tr key={wk.week} className="border-b border-border/30">
                        <td className="py-2 text-foreground">{wk.week} <span className="text-muted-foreground">({wk.start.slice(5)}–{wk.end.slice(5)})</span></td>
                        <td className="py-2 text-center text-foreground">{wk.total}</td>
                        <td className="py-2 text-center text-[#A8E6CF]">{wk.completed}</td>
                        <td className="py-2 text-center">
                          <span className={`px-1.5 py-0.5 rounded-full ${wk.completion_rate >= 80 ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : wk.completion_rate >= 50 ? 'bg-amber-500/10 text-amber-400' : 'bg-red-500/10 text-red-400'}`}>
                            {wk.completion_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Caregiver Utilisation */}
      <Card className="glass-card border-border" data-testid="utilisation-card">
        <CardHeader>
          <CardTitle className="text-base text-foreground flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#8B5CF6]" />Caregiver Utilisation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="p-3 rounded-lg bg-muted border border-border text-center">
              <p className="text-xl font-bold text-[#00A6A0]">{util.avg_hours}h</p>
              <p className="text-[10px] text-muted-foreground mt-1">Avg Hours</p>
            </div>
            <div className="p-3 rounded-lg bg-muted border border-border text-center">
              <p className="text-xl font-bold text-[#FF6F61]">{util.avg_shifts}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Avg Shifts</p>
            </div>
            <div className="p-3 rounded-lg bg-muted border border-border text-center">
              <p className="text-xl font-bold text-[#A8E6CF]">{util.avg_completion}%</p>
              <p className="text-[10px] text-muted-foreground mt-1">Avg Completion</p>
            </div>
            <div className="p-3 rounded-lg bg-muted border border-border text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <UserCheck className="w-3.5 h-3.5 text-[#00A6A0]" />
              </div>
              <p className="text-xl font-bold text-foreground">{util.active_caregivers}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Active</p>
            </div>
            <div className="p-3 rounded-lg bg-muted border border-border text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <UserX className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <p className="text-xl font-bold text-amber-400">{util.idle_caregivers}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Idle</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shift Cost Tracking */}
      <Card className="glass-card border-border" data-testid="shift-costs-card">
        <CardHeader>
          <CardTitle className="text-base text-foreground flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-400" />Shift Cost Tracking
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg bg-muted border border-border text-center">
              <p className="text-xl font-bold text-emerald-400">${costs.total_estimated_cost?.toFixed(2) || '0.00'}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Total Estimated</p>
            </div>
            <div className="p-3 rounded-lg bg-muted border border-border text-center">
              <p className="text-xl font-bold text-[#A8E6CF]">${costs.completed_cost?.toFixed(2) || '0.00'}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Completed</p>
            </div>
            <div className="p-3 rounded-lg bg-muted border border-border text-center">
              <p className="text-xl font-bold text-blue-400">${costs.projected_cost?.toFixed(2) || '0.00'}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Projected (Open)</p>
            </div>
            <div className="p-3 rounded-lg bg-muted border border-border text-center">
              <p className="text-xl font-bold text-foreground">${costs.avg_cost_per_shift?.toFixed(2) || '0.00'}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Avg / Shift</p>
            </div>
          </div>
          {costs.shifts_with_rate === 0 && (
            <p className="text-xs text-muted-foreground text-center">No shifts with hourly rates set for this period. Add a rate when creating shifts to see cost estimates.</p>
          )}
          {(costs.top_caregiver_costs || []).length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground font-medium mb-2">Top Cost by Caregiver</p>
              <div className="space-y-2">
                {costs.top_caregiver_costs.map((tc, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-background/50 border border-border">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-[#FF6F61] w-5">#{i + 1}</span>
                      <div>
                        <p className="text-sm text-foreground">{tc.name}</p>
                        <p className="text-[10px] text-muted-foreground">{tc.shifts} shifts &bull; {tc.hours}h</p>
                      </div>
                    </div>
                    <p className="text-sm font-semibold text-emerald-400">${tc.cost.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Shift Breakdown */}
      <Card className="glass-card border-border">
        <CardHeader>
          <CardTitle className="text-base text-foreground flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#FF6F61]" />Shift Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3 text-center">
            {[
              { label: 'Total', value: s.total, color: 'text-foreground' },
              { label: 'Completed', value: s.completed, color: 'text-[#A8E6CF]' },
              { label: 'Cancelled', value: s.cancelled, color: 'text-amber-400' },
              { label: 'No-Show', value: s.no_show, color: 'text-red-400' },
              { label: 'Open', value: s.open, color: 'text-blue-400' },
            ].map(item => (
              <div key={item.label} className="p-3 rounded-lg bg-muted border border-border">
                <p className={`text-xl font-bold ${item.color}`}>{item.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{item.label}</p>
              </div>
            ))}
          </div>
          {/* Visual bar */}
          {s.total > 0 && (
            <div className="mt-4 h-4 rounded-full overflow-hidden flex bg-muted">
              <div style={{ width: `${(s.completed / s.total) * 100}%` }} className="bg-[#A8E6CF]" title="Completed" />
              <div style={{ width: `${(s.cancelled / s.total) * 100}%` }} className="bg-amber-500" title="Cancelled" />
              <div style={{ width: `${(s.no_show / s.total) * 100}%` }} className="bg-red-500" title="No-Show" />
              <div style={{ width: `${(s.open / s.total) * 100}%` }} className="bg-blue-500" title="Open" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Caregivers */}
      <Card className="glass-card border-border">
        <CardHeader>
          <CardTitle className="text-base text-foreground">Top Caregivers by Hours</CardTitle>
        </CardHeader>
        <CardContent>
          {data.caregiver_stats.length === 0 ? (
            <p className="text-sm text-muted-foreground">No caregiver data for this period</p>
          ) : (
            <div className="space-y-3">
              {data.caregiver_stats.map((cg, i) => (
                <div key={cg.id} className="flex items-center gap-4 p-3 rounded-lg bg-muted border border-border">
                  <span className="text-sm font-bold text-[#FF6F61] w-6">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground font-medium truncate">{cg.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {cg.total_shifts} shifts | {cg.completion_rate}% completion
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-foreground">{cg.total_hours}h</p>
                    <p className="text-xs text-muted-foreground">{cg.completed} completed</p>
                  </div>
                  {/* Mini bar */}
                  <div className="w-16 h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full bg-[#A8E6CF]"
                      style={{ width: `${cg.completion_rate}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
